import { Autocomplete, Button, Chip, TextField, MenuItem, Select, FormControl, InputLabel, Typography, Box, Stack, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import React from 'react';
import TableToolbar, { TableToolbarProps } from '../../components/table/TableToolbar';
import { UserPost } from '../../userFetch';
import Device from '../../models/device';
import { useContextSelector } from 'use-context-selector';
import { DevicesContext } from './deviceContext';
import useOwnerNames from '../../hooks/useOwnerNames';
import { onWsEvent } from '../../websocket';

// Add the style for the animation once when the module is loaded
const pulseAnimation = `
@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    opacity: 1;
  }
}`;

// Use an IIFE to add the style to the document only once
(function addStyleOnce() {
    if (!document.getElementById('pulse-animation-style')) {
        const styleElement = document.createElement('style');
        styleElement.id = 'pulse-animation-style';
        styleElement.innerHTML = pulseAnimation;
        document.head.appendChild(styleElement);
    }
})();

export type DeviceStateFilter = 'active' | 'inactive' | 'all' | 'issues' | 'pending';

interface DeviceTableToolbarProps extends TableToolbarProps {
    onExecuteAction: (action: (serial_number: string, device: Device) => Promise<never>) => void;
    onBulkFullupdate?: (payload: string) => Promise<void>;
    activeState?: DeviceStateFilter;
    onActiveStateChange?: (state: DeviceStateFilter) => void;
    issuesCount?: number;
    pendingCount?: number;
    activeCount?: number;
    inactiveCount?: number;
    visibleFilters?: (DeviceStateFilter)[];
    isSuperAdmin?: boolean;
    bulkInputRef?: React.MutableRefObject<HTMLInputElement | null>;
}

interface BulkFullupdateStatus {
    owner: string;
    active: string | null;
    queueLength: number;
    completed: number;
    failed: number;
}

function DeviceTableToolbar(props: DeviceTableToolbarProps) {
    const { ownerNames } = useOwnerNames();
    const autoRefreshActive = useContextSelector(
        DevicesContext,
        (context) => Object.keys(context.state.ongoingCommand).length > 0
    );
    const devicesDispatch = useContextSelector(DevicesContext, (c) => c.dispatch);

    const [command, setCommand] = React.useState('');
    const [owner, setOwner] = React.useState('');
    const [payload, setPayload] = React.useState('');
    const [tags, setTags] = React.useState('');
    const [location, setLocation] = React.useState('');
    const [toolbarType, setToolbarType] = React.useState<'command' | 'owner' | 'setTags' | 'clearTags' | 'location' | 'addTags' | 'removeTags' | ''>(() => props.isSuperAdmin ? 'command' : 'addTags');
    const [bulkStatus, setBulkStatus] = React.useState<BulkFullupdateStatus | null>(null);

    // Live status from the server-side fullupdate queue. Clear the chip once the rollout
    // has fully drained (no active device, no queue, no in-flight counts).
    React.useEffect(() => {
        return onWsEvent('bulk:fullupdate:status', (msg: any) => {
            const status = msg as BulkFullupdateStatus;
            if (!status.active && status.queueLength === 0 && status.completed === 0 && status.failed === 0) {
                setBulkStatus(null);
            } else {
                setBulkStatus(status);
            }
        });
    }, []);

    const triggerCommand = () => {
        if (command === 'fullupdate' && props.onBulkFullupdate) {
            // Route bulk fullupdate through the server-side per-owner queue (one device
            // at a time) so concurrent downloads don't saturate the LAN / S3 / cache peers.
            props.onBulkFullupdate(payload);
        } else {
            props.onExecuteAction(commandAction);
        }
        setCommand('');
        setPayload('');
    };

    const commandAction = React.useCallback((serialNumber: string) => {
        return UserPost(`/api/devices/${serialNumber}/command`,
            {
                operation: command,
                payload: payload
            }).then(() => {
                devicesDispatch({
                    type: 'SENDING_COMMAND',
                    payload: [{
                        serial_number: serialNumber,
                        command
                    }]
                });
            }) as Promise<never>;
    }, [command, payload]);

    const ownerAction = React.useCallback((serialNumber: string) => {
        return UserPost('/api/devices/',
            {
                serial_number: serialNumber,
                owner: owner
            }) as Promise<never>;
    }, [owner]);

    const setTagsAction = React.useCallback((serialNumber: string) => {
        return UserPost('/api/devices/',
            {
                serial_number: serialNumber,
                tags: tags
            }) as Promise<never>;
    }, [tags]);

    const addTagsAction = React.useCallback((serialNumber: string, device?: Device) => {
        const newTags = Array.from(new Set([...(device?.tags?.split(' ') || []), ...tags.split(' ').map((t) => t.trim())]));
        return UserPost('/api/devices/',
            {
                serial_number: serialNumber,
                tags: newTags.join(' ')
            }) as Promise<never>;
    }, [tags]);

    const removeTagsAction = React.useCallback((serialNumber: string, device?: Device) => {
        const tagsToRemove = tags.split(' ').map((t) => t.trim());
        const newTags = (device?.tags?.split(' ') || []).filter((t) => !tagsToRemove.includes(t));

        return UserPost('/api/devices/',
            {
                serial_number: serialNumber,
                tags: newTags.join(' ')
            }) as Promise<never>;
    }, [tags]);


    const clearTagsAction = React.useCallback((serialNumber: string) => {
        return UserPost('/api/devices/',
            {
                serial_number: serialNumber,
                tags: ''
            }) as Promise<never>;
    }, []);

    const locationAction = React.useCallback((serialNumber: string) => {
        return UserPost('/api/devices/',
            {
                serial_number: serialNumber,
                location: location,
            }) as Promise<never>;
    }, [location]);

    const renderCommandToolbar = () => {
        return (
            <>
                <TextField
                    margin="none"
                    size="small"
                    label="Command"
                    value={command}
                    onChange={(e) => setCommand(e.target.value)}
                    onKeyUp={(e) => {
                        if (e.key === 'Enter') triggerCommand();
                    }}
                    inputRef={props.bulkInputRef}
                    sx={{ minWidth: '250px' }}
                />
                <TextField
                    size="small"
                    label="Payload"
                    value={payload}
                    onChange={(e) => setPayload(e.target.value)}
                    multiline
                    rows={3}
                    sx={{ minWidth: '250px' }}
                />
                <Button
                    variant="contained"
                    onClick={triggerCommand}
                    disabled={!command}
                    size="small"
                >
                    Apply
                </Button>
                {bulkStatus && (
                    <Chip
                        size="small"
                        color={bulkStatus.active ? 'warning' : 'success'}
                        label={
                            bulkStatus.active
                                ? `${bulkStatus.owner} fullupdate: ${bulkStatus.active} (${bulkStatus.completed + bulkStatus.failed + 1} of ${bulkStatus.completed + bulkStatus.failed + 1 + bulkStatus.queueLength}${bulkStatus.failed > 0 ? `, ${bulkStatus.failed} failed` : ''})`
                                : `${bulkStatus.owner} fullupdate done: ${bulkStatus.completed} ok${bulkStatus.failed > 0 ? `, ${bulkStatus.failed} failed` : ''}`
                        }
                        onDelete={!bulkStatus.active ? () => setBulkStatus(null) : undefined}
                    />
                )}
            </>);
    }

    const renderOwnerToolbar = () => {
        const handleApply = () => {
            props.onExecuteAction(ownerAction);
            setOwner('');
        };

        return (<>
            <Autocomplete
                options={ownerNames}
                value={owner || null}
                onChange={(_e, value) => setOwner(value || '')}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        margin="dense"
                        size="small"
                        label="Owner"
                        inputRef={props.bulkInputRef}
                    />
                )}
                sx={{ minWidth: '250px' }}
                size="small"
            />
            <Button
                variant="contained"
                onClick={handleApply}
                size="small"
            >
                Apply
            </Button>
        </>);
    }

    const renderLocationToolbar = () => {
        const handleApply = () => {
            props.onExecuteAction(locationAction);
            setLocation('');
        };

        return (<>
            <TextField
                margin="dense"
                size="small"
                label="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApply();
                    }
                }}
                inputRef={props.bulkInputRef}
                sx={{ minWidth: '250px' }}
            />
            <Button
                variant="contained"
                onClick={handleApply}
                size="small"
            >
                Apply
            </Button>
        </>);
    }

    const renderSetTagsToolbar = () => {
        const handleApply = () => {
            props.onExecuteAction(setTagsAction);
            setTags('');
        };

        return (<>
            <TextField
                margin="dense"
                size="small"
                label="Tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApply();
                    }
                }}
                inputRef={props.bulkInputRef}
                sx={{ minWidth: '250px' }}
            />
            <Button
                variant="contained"
                onClick={handleApply}
                size="small"
            >
                Apply
            </Button>
        </>);
    }

    const renderAddTagsToolbar = () => {
        const handleApply = () => {
            props.onExecuteAction(addTagsAction);
            setTags('');
        };

        return (<>
            <TextField
                margin="dense"
                size="small"
                label="Tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApply();
                    }
                }}
                inputRef={props.bulkInputRef}
                sx={{ minWidth: '250px' }}
            />
            <Button
                variant="contained"
                onClick={handleApply}
                size="small"
            >
                Apply
            </Button>
        </>);
    }

    const renderRemoveTagsToolbar = () => {
        const handleApply = () => {
            props.onExecuteAction(removeTagsAction);
            setTags('');
        };

        return (<>
            <TextField
                margin="dense"
                size="small"
                label="Tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApply();
                    }
                }}
                inputRef={props.bulkInputRef}
                sx={{ minWidth: '250px' }}
            />
            <Button
                variant="contained"
                onClick={handleApply}
                size="small"
            >
                Apply
            </Button>
        </>);
    }

    const renderClearTagsToolbar = () => {
        return (
            <Button
                variant="contained"
                onClick={() => {
                    props.onExecuteAction(clearTagsAction);
                    setTags('');
                }}
                size="small"
            >
                Clear
            </Button>
        );
    }

    const showFilter = (f: DeviceStateFilter) => !props.visibleFilters || props.visibleFilters.includes(f);

    return (
        <TableToolbar
            {... (props as TableToolbarProps)}
            title={<Stack direction="row" spacing={0.5} alignItems="center" sx={{ display: 'inline-flex' }}>
                <Typography
                    variant="h6"
                    sx={{ flexGrow: 1, minWidth: 80, fontWeight: 600 }}
                    component="div">
                    Devices</Typography>
                {props.activeState && (
                    <RadioGroup
                        row
                        value={props.activeState}
                        onChange={(e) => {
                            props.onActiveStateChange?.(e.target.value as DeviceStateFilter);
                        }}
                        sx={{ ml: 1 }}>
                        {/* Radios not in visibleFilters are hidden (parent decides: Active/Inactive
                            only when the fleet is split, Issues only when there are some). */}
                        <FormControlLabel
                            value="all"
                            control={<Radio size="small" sx={{ py: 0 }} />}
                            label={<Typography sx={{ fontSize: '0.9rem' }}>All</Typography>}
                            sx={{ mr: { xs: 1, sm: '30px' } }}
                        />
                        {showFilter('inactive') && (
                            <FormControlLabel
                                value="inactive"
                                control={<Radio size="small" sx={{ py: 0 }} />}
                                label={<><Typography sx={{ fontSize: '0.9rem', display: { xs: 'none', sm: 'block' } }}>Inactive{props.inactiveCount !== undefined ? ` (${props.inactiveCount})` : ''}</Typography><Typography sx={{ fontSize: '0.8rem', display: { xs: 'block', sm: 'none' } }}>Inact{props.inactiveCount !== undefined ? ` (${props.inactiveCount})` : ''}</Typography></>}
                                sx={{ mr: { xs: 1, sm: '30px' } }}
                            />
                        )}
                        {showFilter('active') && (
                            <FormControlLabel
                                value="active"
                                control={<Radio size="small" sx={{ py: 0 }} />}
                                label={<><Typography sx={{ fontSize: '0.9rem', display: { xs: 'none', sm: 'block' } }}>Active{props.activeCount !== undefined ? ` (${props.activeCount})` : ''}</Typography><Typography sx={{ fontSize: '0.8rem', display: { xs: 'block', sm: 'none' } }}>Act{props.activeCount !== undefined ? ` (${props.activeCount})` : ''}</Typography></>}
                                sx={{ mr: { xs: 1, sm: '30px' } }}
                            />
                        )}
                        {showFilter('issues') && (
                            <FormControlLabel
                                value="issues"
                                control={<Radio size="small" sx={{ py: 0 }} color="warning" />}
                                label={<Typography sx={{ fontSize: '0.9rem', color: 'warning.main', fontWeight: 600 }}>⚠ Issues ({props.issuesCount})</Typography>}
                                sx={{ mr: { xs: 1, sm: '30px' } }}
                            />
                        )}
                        {showFilter('pending') && (
                            <FormControlLabel
                                value="pending"
                                control={<Radio size="small" sx={{ py: 0 }} color="info" />}
                                label={<Typography sx={{ fontSize: '0.9rem', color: 'info.main', fontWeight: 600 }}>⏳ Pending ({props.pendingCount})</Typography>}
                                sx={{ mr: { xs: 1, sm: '30px' } }}
                            />
                        )}
                    </RadioGroup>
                )}
                {autoRefreshActive && (
                    <Typography
                        variant="caption"
                        title="Show devices with a pending command"
                        onClick={() => props.onActiveStateChange?.('pending')}
                        sx={{
                            ml: 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            color: 'text.secondary',
                            animation: 'pulse 2s infinite',
                            fontSize: '0.7rem',
                            cursor: 'pointer'
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                height: 6,
                                width: 6,
                                borderRadius: '50%',
                                bgcolor: 'success.main',
                                mr: 0.5,
                                display: 'inline-block'
                            }}
                        />
                        Auto-refreshing commands...
                    </Typography>)
                }</Stack>}
            selectActionBar={
                <Stack direction="row" spacing={1} alignItems="center" justifyItems="flex-start">
                    <FormControl size="small" sx={{ m: 0, minWidth: 100 }}>
                        <InputLabel id="toolbar-type-select-label" sx={{ fontSize: '0.8125rem' }}>Action</InputLabel>
                        <Select
                            labelId="toolbar-type-select-label"
                            value={toolbarType}
                            onChange={(e) => setToolbarType(e.target.value as 'command' | 'owner')}
                            autoWidth
                            label="Action"
                            sx={{ fontSize: '0.8125rem', height: '32px' }}
                        >
                            {props.isSuperAdmin && <MenuItem value="command" sx={{ fontSize: '0.8125rem' }}>Send Command</MenuItem>}
                            <MenuItem value="addTags" sx={{ fontSize: '0.8125rem' }}>Add Tags</MenuItem>
                            <MenuItem value="removeTags" sx={{ fontSize: '0.8125rem' }}>Remove Tags</MenuItem>
                            {props.isSuperAdmin && <MenuItem value="owner" sx={{ fontSize: '0.8125rem' }}>Set Owner</MenuItem>}
                            <MenuItem value="location" sx={{ fontSize: '0.8125rem' }}>Set Location</MenuItem>
                            <MenuItem value="setTags" sx={{ fontSize: '0.8125rem' }}>Set All Tags</MenuItem>
                            <MenuItem value="clearTags" sx={{ fontSize: '0.8125rem' }}>Wipe All Tags</MenuItem>
                        </Select>
                    </FormControl>
                    {toolbarType === 'command' && renderCommandToolbar()}
                    {toolbarType === 'addTags' && renderAddTagsToolbar()}
                    {toolbarType === 'removeTags' && renderRemoveTagsToolbar()}
                    {toolbarType === 'owner' && renderOwnerToolbar()}
                    {toolbarType === 'location' && renderLocationToolbar()}
                    {toolbarType === 'setTags' && renderSetTagsToolbar()}
                    {toolbarType === 'clearTags' && renderClearTagsToolbar()}
                </Stack>
            }

        />
    );
}

export default DeviceTableToolbar;